import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Scale, Shield, FileText, Mail } from 'lucide-react';

// IMPORT THE IMAGE FROM YOUR ASSETS FOLDER HERE
// Adjust the '../assets/' path depending on where this component file is located
import termsImage from '../assets/Terms_Img.png';

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';

/* Resize the hero image with the values below. On desktop it's an ABSOLUTE overlay,
   so making it bigger only overlaps — the text and everything below never move.
   (If you enlarge it a lot and it clips, bump the `lg:min-h-[380px]` on its wrapper div.) */
const HERO_IMAGE = {
  desktopWidth: '500px',   // ← adjust width as needed
  desktopHeight: 'auto',
  offsetX: '0px',
  offsetY: '0px',
};

const TermsConditions = () => {
  const lastUpdated = "October 6, 2026";

  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      <style>{`
        /* Hero image: absolute overlay on desktop so resizing it never shifts the layout */
        @media (min-width: 1024px) {
          .tc-hero-img {
            position: absolute;
            top: calc(50% + var(--hero-y, 0px));
            right: var(--hero-x, 0px);
            transform: translateY(-50%);
            width: var(--hero-w, 440px);
            height: var(--hero-h, auto);
            max-width: none;
            margin: 0;
          }
        }
      `}</style>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white">
        <div className={`${CONTAINER} relative py-10 lg:py-12`}>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            
            {/* Left Content */}
            <div>
              <nav className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[#18469A] font-medium">Terms & Conditions</span>
              </nav>
              <span className="block text-xs font-bold tracking-[0.2em] uppercase text-[#18469A] mb-3">Legal Information</span>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Terms & Conditions</h1>
              <p className="mt-4 text-lg text-slate-500 max-w-lg leading-relaxed">
                Please read these terms carefully before using our software, products, and services. 
              </p>
            </div>

            {/* Right Image */}
            <div className="hidden sm:block relative lg:min-h-[380px]">
              <img
                src={termsImage} // <-- USE THE IMPORTED VARIABLE HERE
                alt="Terms and Conditions"
                className="tc-hero-img w-full h-auto max-w-[440px] mx-auto"
                draggable="false"
                style={{
                  '--hero-w': HERO_IMAGE.desktopWidth,
                  '--hero-h': HERO_IMAGE.desktopHeight,
                  '--hero-x': HERO_IMAGE.offsetX,
                  '--hero-y': HERO_IMAGE.offsetY,
                }}
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
            </div>

          </div>
        </div>
      </section>

      {/* ================= CONTENT ================= */}
      <section className={`${CONTAINER} py-10 pb-20`}>
        <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-start">
          
          {/* Main Legal Text */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-6 sm:p-10">
            <div className="prose prose-slate max-w-none">
              <p className="text-sm text-slate-500 mb-8 font-medium">Last Updated: {lastUpdated}</p>

              <div className="space-y-8 text-slate-600 leading-relaxed text-[15px]">
                
                {/* Section 1 */}
                <section>
                  <h2 className="text-xl font-extrabold text-[#13294b] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#18469A]" />
                    1. Agreement to Terms
                  </h2>
                  <p>
                    By accessing or using the services provided by SmartEdz Technologies / SmartEdz LLP ("Company", "we", "us", or "our"), 
                    including our ERP systems, AI solutions, mobile applications, and web development services, you agree to be bound by these 
                    Terms and Conditions. If you disagree with any part of the terms, you may not access our services.
                  </p>
                </section>

                {/* Section 2 */}
                <section>
                  <h2 className="text-xl font-extrabold text-[#13294b] mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#18469A]" />
                    2. Intellectual Property Rights
                  </h2>
                  <p>
                    Other than the content you own, under these Terms, SmartEdz and/or its licensors own all the intellectual property rights 
                    and materials contained in our products and software solutions. You are granted limited license only for purposes of viewing 
                    and utilizing the software solutions as per your specific service level agreement (SLA) or subscription plan.
                  </p>
                </section>

                {/* Section 3 */}
                <section>
                  <h2 className="text-xl font-extrabold text-[#13294b] mb-4 flex items-center gap-2">
                    <Scale className="w-5 h-5 text-[#18469A]" />
                    3. User Responsibilities & Restrictions
                  </h2>
                  <p className="mb-3">You are specifically restricted from all of the following:</p>
                  <ul className="list-disc pl-5 space-y-2 mb-4">
                    <li>Publishing any SmartEdz software material in any other media without prior consent.</li>
                    <li>Selling, sublicensing, and/or otherwise commercializing any of our proprietary ERP or AI solutions.</li>
                    <li>Using our services in any way that is or may be damaging to the software infrastructure or servers.</li>
                    <li>Engaging in any data mining, data harvesting, data extracting, or any other similar activity in relation to our applications.</li>
                  </ul>
                </section>

                {/* Section 4 */}
                <section>
                  <h2 className="text-xl font-extrabold text-[#13294b] mb-4">4. Limitation of Liability</h2>
                  <p>
                    In no event shall SmartEdz LLP, nor any of its officers, directors, and employees, be held liable for anything arising 
                    out of or in any way connected with your use of our software solutions, whether such liability is under contract. 
                    SmartEdz LLP shall not be held liable for any indirect, consequential, or special liability arising out of or in any 
                    way related to your use of our services or business operations.
                  </p>
                </section>

                {/* Section 5 */}
                <section>
                  <h2 className="text-xl font-extrabold text-[#13294b] mb-4">5. Governing Law & Jurisdiction</h2>
                  <p>
                    These Terms will be governed by and interpreted in accordance with the laws of India. Any disputes relating to these 
                    terms and conditions will be subject to the exclusive jurisdiction of the courts located in <strong>Hyderabad, Telangana</strong>.
                  </p>
                </section>

              </div>
            </div>
          </div>

          {/* Sidebar / Quick Contact */}
          <div className="space-y-6 lg:sticky lg:top-8">
            <div className="bg-[#f5f9fd] rounded-2xl ring-1 ring-slate-200 p-6">
              <h3 className="font-extrabold text-[#13294b] mb-2">Have Questions?</h3>
              <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                If you have any questions about these Terms, please contact our legal and support team.
              </p>
              
              <ul className="space-y-4">
                <li>
                  <a href="mailto:support@smartedz.com" className="flex items-center gap-3 text-sm font-medium text-slate-700 hover:text-[#18469A] transition-colors group">
                    <span className="w-10 h-10 rounded-full bg-white ring-1 ring-slate-200 flex items-center justify-center shrink-0 group-hover:ring-[#18469A]/30">
                      <Mail className="w-4 h-4 text-[#18469A]" />
                    </span>
                    support@smartedz.com
                  </a>
                </li>
                <li>
                  <Link to="/contact" className="flex items-center justify-center w-full bg-white ring-1 ring-slate-200 hover:ring-[#18469A] text-slate-700 hover:text-[#18469A] rounded-lg px-4 py-2.5 text-sm font-semibold transition-all shadow-sm">
                    Contact Support
                  </Link>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 flex items-start gap-4">
               <Shield className="w-6 h-6 text-green-600 shrink-0 mt-1" />
               <div>
                 <h4 className="font-bold text-slate-800 text-sm mb-1">Privacy Assured</h4>
                 <p className="text-xs text-slate-500 leading-relaxed">
                   We take your privacy seriously. Read our <Link to="/privacy-policy" className="text-[#18469A] hover:underline">Privacy Policy</Link> to learn how we handle your data.
                 </p>
               </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default TermsConditions;